import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Box, CircularProgress, Typography, Alert } from '@mui/material';
import authApi from '../services/authApi';
import sharePointTokenService from '../services/sharePointTokenService';

const AuthCallback = () => {
  const [status, setStatus] = useState('processing');
  const [error, setError] = useState(null);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const processAuthCallback = async () => {
      // Get the authorization code and state from the URL
      const queryParams = new URLSearchParams(location.search);
      const code = queryParams.get('code');
      const state = queryParams.get('state');
      const error = queryParams.get('error');
      const errorDescription = queryParams.get('error_description');

      if (error) {
        setStatus('error');
        const errorMsg = `Authentication failed: ${error} - ${errorDescription}`;
        console.error(errorMsg);
        setError(errorMsg);
        return;
      }

      if (!code || !state) {
        setStatus('error');
        const errorMsg = 'Missing authorization code or state parameter';
        console.error(errorMsg);
        setError(errorMsg);
        return;
      }

      try {
        console.log('Raw state parameter:', state);
        
        // Try different approaches to extract configId from state
        let configId;
        
        // Approach 1: New format we've implemented: randomString-configId
        if (state.includes('-')) {
          const parts = state.split('-');
          const lastPart = parts[parts.length - 1];
          if (/^\d+$/.test(lastPart)) {
            configId = parseInt(lastPart, 10);
            console.log('Extracted configId from new hyphenated state format:', configId);
          }
        }
        // Approach 2: Sometimes Microsoft just returns our original state unchanged
        else if (/^\d+$/.test(state)) {
          // State is just a number - likely our configId
          configId = parseInt(state, 10);
          console.log('Extracted configId directly from numeric state:', configId);
        }
        // Approach 3: Check if it's our old mock format
        else if (state.includes('mock-state-')) {
          configId = state.split('mock-state-')[1];
          if (configId && /^\d+$/.test(configId)) {
            configId = parseInt(configId, 10);
            console.log('Extracted configId from mock-state format:', configId);
          }
        }
        
        // If we couldn't extract the configId, show an error
        if (!configId) {
          setStatus('error');
          const errorMsg = 'Could not determine the SharePoint configuration ID from the state parameter. Please try again or contact support.';
          console.error(errorMsg, { state });
          setError(errorMsg);
          return;
        }
        
        console.log('Processing authorization callback with:', {
          code: code.substring(0, 5) + '...',
          state,
          configId
        });
        
        if (!configId) {
          setStatus('error');
          setError('Could not determine the SharePoint configuration to use. Please try again.');
          return;
        }
        
        // Exchange the code for tokens (use app JWT)
        const token = authApi.getToken();
        
        try {
          // Try using the token service first
          const response = await sharePointTokenService.exchangeCodeForTokens(token, { 
            code, 
            state,
            configId 
          });
          
          if (!response.success) {
            throw new Error(response.message || 'Failed to exchange code for tokens');
          }
          
          console.log('Successfully exchanged code for tokens:', response);
          setStatus('success');
        } catch (serviceError) {
          console.error('Error using service for token exchange:', serviceError);
          
          // Fallback to direct API call if service fails
          const apiResponse = await fetch('/api/sharepoint/exchange-code', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${token}`
            },
            body: JSON.stringify({ 
              code, 
              state,
              configId 
            })
          });
          
          if (!apiResponse.ok) {
            const errorData = await apiResponse.text();
            console.error('API error response:', errorData);
            throw new Error(`API error: ${apiResponse.status} ${apiResponse.statusText}`);
          }
          
          const data = await apiResponse.json();
          if (!data.success) {
            throw new Error(data.message || 'Failed to exchange code for tokens');
          }
          
          console.log('Successfully exchanged code for tokens through API call:', data);
          setStatus('success');
        }
        
        // After a short delay, close this window if it's a popup or redirect
        setTimeout(() => {
          if (window.opener) {
            window.close();
          } else {
            // If not a popup, navigate back to the token manager
            navigate('/sharepoint-tokens');
          }
        }, 3000);
      } catch (error) {
        console.error('Error exchanging code for authentication:', error);
        setStatus('error');
        setError(error.message || 'An error occurred during token exchange');
      }
    };

    processAuthCallback();
  }, [location, navigate]);

  return (
    <Box 
      sx={{ 
        display: 'flex', 
        flexDirection: 'column',
        alignItems: 'center', 
        justifyContent: 'center', 
        minHeight: '100vh',
        p: 2,
        textAlign: 'center'
      }}
    >
      {status === 'processing' && (
        <>
          <CircularProgress size={50} sx={{ mb: 2 }} />
          <Typography variant="h5" gutterBottom>Processing Authentication</Typography>
          <Typography variant="body1" color="textSecondary">
            Please wait while we complete the authentication process...
          </Typography>
        </>
      )}

      {status === 'success' && (
        <>
          <Alert severity="success" sx={{ mb: 2, maxWidth: 500 }}>
            Authentication successful!
          </Alert>
          <Typography variant="body1">
            You can close this window or you will be redirected shortly.
          </Typography>
        </>
      )}

      {status === 'error' && (
        <>
          <Alert severity="error" sx={{ mb: 2, maxWidth: 500 }}>
            {error || 'An unknown error occurred'}
          </Alert>
          <Typography variant="body1">
            Please close this window and try again.
          </Typography>
        </>
      )}
    </Box>
  );
};

export default AuthCallback; 